import { apiAssetUrl, filenameFromContentDisposition, loginRedirectTarget } from "./api";

/**
 * Salva un blob su disco con un anchor temporaneo.
 *
 * La revoca dell'object URL è DIFFERITA di proposito: il download parte in modo
 * asincrono e revocare subito può invalidare il blob prima che venga letto —
 * cioè un download che «non funziona sempre».
 */
export function saveBlob(blob: Blob, fileName: string): void {
  const objUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(objUrl), 60_000);
}

/**
 * Scarica un file dal backend via fetch + blob. Unico posto dove vive questo
 * pattern.
 *
 * Prima era copiato in fatturazione, ordini (modulo SDA), resi e messaggi, e le
 * copie **divergevano**: solo quella di fatturazione faceva il redirect al login
 * sul 401 e leggeva il ``detail`` italiano del backend. Le altre lanciavano il
 * codice HTTP nudo (``[dispute-file] 401``), che ``prettifyApiError`` non
 * riconosce e mostrava tale e quale all'operatore.
 *
 * Il nome file lo detta il backend (``Content-Disposition``, autoritativo) con
 * fallback locale: in dev cross-origin quell'header non è esposto.
 *
 * ``url`` resta sovrascrivibile perché in DEV ``apiAssetUrl`` punta al loopback
 * assoluto, mentre ``apiUrl`` resta relativo e passa dal proxy Vite — che è
 * quello che porta i cookie di sessione.
 */
export async function downloadFile(
  path: string,
  fallbackName: string,
  url: string = apiAssetUrl(path),
): Promise<void> {
  const res = await fetch(url);
  if (!res.ok) {
    // Stessa guardia di ``raiseApiError``: in web-mode un 401 (sessione
    // scaduta) porta alla login invece che a un download vuoto o a un errore
    // opaco.
    const target = loginRedirectTarget(
      res.status,
      path,
      window.location.pathname + window.location.search,
    );
    if (target) window.location.assign(target);
    // Il ``detail`` del backend è già una frase italiana che dice cosa manca
    // («nessun documento da stampare per questa riga»).
    let dettaglio = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as { detail?: string; error?: { message?: string } };
      dettaglio = body.error?.message ?? body.detail ?? dettaglio;
    } catch {
      // Corpo non-JSON (pagina d'errore del proxy): resta lo status, che
      // ``prettifyApiError`` traduce comunque — a patto che il prefisso sia il
      // path ``/api/...``, come qui.
    }
    throw new Error(`[${path}] ${dettaglio}`);
  }
  saveBlob(
    await res.blob(),
    filenameFromContentDisposition(res.headers.get("Content-Disposition")) ?? fallbackName,
  );
}
